// Build a fraction-sentence visual (shaded bars for each given fraction) straight
// from a fraction word problem's text, mirroring the backend's _auto_fraction_sentence.
// This runs on the client as a fallback so the scaffold shows even if the backend
// build hasn't caught up. Conservative on purpose: only a clear "combine the givens"
// addition problem (a total/sum cue and no subtraction/multi-step cue), always "+".
export function autoFractionSentence(question) {
  const q = String(question || "");
  if (!/\b(in total|total|altogether|combined|in all|together|sum)\b/i.test(q)) return null;
  if (/\b(how much more|how much less|left over|left|remain\w*|difference|more than|less than|fewer|greater|between)\b/i.test(q)) {
    return null;
  }
  let fracs = [...q.matchAll(/\\[dt]?frac\s*\{(\d+)\}\s*\{(\d+)\}/g)].map((m) => [Number(m[1]), Number(m[2])]);
  if (fracs.length < 2) {
    fracs = [...q.matchAll(/\b(\d+)\s*\/\s*(\d+)\b/g)].map((m) => [Number(m[1]), Number(m[2])]);
  }
  fracs = fracs.filter(([, d]) => d >= 2).slice(0, 3);
  if (fracs.length < 2) return null;
  return {
    kind: "fraction-sentence",
    fractions: fracs.map(([n, d]) => ({ numerator: n, denominator: d })),
    operator: "+",
  };
}

// Strip-model (bar model) fallback for WHOLE-NUMBER word problems — the
// non-fraction sibling of autoFractionSentence, mirroring the backend's
// _auto_strip_model. Conservative: clear combine problems (2-3 numbers + a
// total cue) or clear take-away problems (exactly 2 numbers + a removal cue).
export function autoStripModel(question) {
  const q = String(question || "");
  if (/\\[dt]?frac|\b\d+\s*\/\s*\d+\b/.test(q)) return null;
  if (/\b(each|every|per|times|twice|double|half|split|shares?d?|equal(?:ly)?|rows? of|groups? of|packs? of|boxes? of)\b/i.test(q)) return null;
  if (/\b(how (?:much|many) (?:more|less|fewer)|more than|less than|fewer|difference|between)\b/i.test(q)) return null;
  const nums = [...q.matchAll(/(?<![\d.])(\d{1,3}(?:,\d{3})*|\d+)(?!\d|\.\d|%)/g)]
    .map((m) => Number(m[1].replace(/,/g, "")))
    .filter((n) => n >= 1 && n <= 2000);
  const combine = /\b(in total|total|altogether|combined|in all|together|sum)\b/i.test(q);
  const takeaway = /\b(how many (?:are |were )?left|left over|remain\w*|gave away|gives? away|ate|eats|lost|loses|sold|sells|used|uses|takes? away|flew away|ran away)\b/i.test(q);
  if (combine && !takeaway) {
    const parts = nums.slice(0, 3);
    if (parts.length < 2) return null;
    return { kind: "strip-model", mode: "combine", parts };
  }
  if (takeaway && !combine) {
    if (nums.length !== 2) return null;
    const total = Math.max(...nums), part = Math.min(...nums);
    if (total === part) return null;
    return { kind: "strip-model", mode: "take-away", total, part };
  }
  return null;
}

// The visualData to actually render for an item: keep a fraction /
// fraction-sentence / strip-model the backend supplied, but for a word problem
// that has no good visual (or a wrong kind) fall back to a client-built
// fraction-sentence (fraction problems) or strip model (whole-number problems).
export function effectiveVisual(type, visualData, question) {
  const isWordProblem = type === "problem-solving" || type === "fill-in-the-blank";
  if (isWordProblem) {
    const kind = visualData && visualData.kind;
    if (kind !== "fraction" && kind !== "fraction-sentence" && kind !== "strip-model") {
      const auto = autoFractionSentence(question) || autoStripModel(question);
      if (auto) return auto;
    }
  }
  return visualData;
}
